import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Bell, BellOff, Check, CheckCheck, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { notificationService } from "@/lib/services";
import { useNotifications } from "@/lib/notification-context";
import { PageShell } from "@/components/nav";
import { formatDateTime } from "@/lib/format";
import { extractErrorMessage } from "@/lib/api";
import type { Notification } from "@/types/api";

export const Route = createFileRoute("/_authenticated/notifications")({
  component: NotificationsPage,
});

function notificationTitle(n: Notification): string {
  const raw = n.type.split("\\").pop() ?? n.type;
  // BookingConfirmed -> Booking Confirmed
  return raw.replace(/([a-z])([A-Z])/g, "$1 $2");
}

function notificationBody(n: Notification): string {
  const data = n.data ?? {};
  if (typeof data.message === "string") return data.message;
  if (typeof data.title === "string") return data.title;
  // fall back to a compact JSON peek
  return Object.entries(data)
    .filter(([k]) => !["id", "type"].includes(k))
    .slice(0, 3)
    .map(([k, v]) => `${k}: ${typeof v === "string" ? v : JSON.stringify(v)}`)
    .join(" · ");
}

function NotificationsPage() {
  const qc = useQueryClient();
  const { refresh } = useNotifications();

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["notifications"],
    queryFn: () => notificationService.list(50),
  });

  const markRead = useMutation({
    mutationFn: (id: string) => notificationService.markRead(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["notifications"] });
      void refresh();
    },
  });

  const markAll = useMutation({
    mutationFn: () => notificationService.markAllRead(),
    onSuccess: () => {
      toast.success("Marked all as read");
      qc.invalidateQueries({ queryKey: ["notifications"] });
      void refresh();
    },
    onError: (err) => toast.error(extractErrorMessage(err, "Couldn't update notifications")),
  });

  const remove = useMutation({
    mutationFn: (id: string) => notificationService.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["notifications"] });
      void refresh();
    },
  });

  const items = data?.notifications ?? [];
  const unread = data?.unread_count ?? 0;

  return (
    <PageShell>
      <div className="mx-auto max-w-3xl px-4 py-10">
        <div className="flex items-end justify-between gap-4">
          <div>
            <h1 className="text-3xl font-extrabold tracking-tight text-ink">Notifications</h1>
            <p className="mt-1 text-sm text-ink-muted">
              {unread > 0 ? `${unread} unread` : "You're all caught up."}
            </p>
          </div>
          {unread > 0 && (
            <button
              onClick={() => markAll.mutate()}
              disabled={markAll.isPending}
              className="inline-flex items-center gap-2 rounded-xl border border-border bg-card px-3 py-2 text-sm font-bold text-ink hover:bg-muted disabled:opacity-60"
            >
              <CheckCheck className="h-4 w-4" /> Mark all read
            </button>
          )}
        </div>

        {isLoading && (
          <div className="mt-6 space-y-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <div
                key={i}
                className="h-20 animate-pulse rounded-2xl border border-border brand-skeleton"
              />
            ))}
          </div>
        )}

        {isError && (
          <div className="mt-6 rounded-2xl border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
            {extractErrorMessage(error, "Couldn't load notifications")}
          </div>
        )}

        {!isLoading && items.length === 0 && (
          <div className="mt-10 rounded-3xl border border-border bg-card p-10 text-center">
            <BellOff className="mx-auto h-8 w-8 text-ink-subtle" />
            <p className="mt-3 text-base font-bold text-ink">No notifications yet</p>
            <p className="mt-1 text-sm text-ink-muted">
              Booking updates and host activity will show up here.
            </p>
            <Link
              to="/"
              className="mt-5 inline-block rounded-xl bg-primary px-4 py-2 text-sm font-bold text-primary-foreground"
            >
              Browse listings
            </Link>
          </div>
        )}

        <ul className="mt-6 space-y-3">
          {items.map((n) => {
            const isUnread = !n.read_at;
            return (
              <li
                key={n.id}
                className={`flex items-start gap-3 rounded-2xl border p-4 transition ${
                  isUnread ? "border-primary/30 bg-primary/5" : "border-border bg-card"
                }`}
              >
                <div
                  className={`grid h-9 w-9 shrink-0 place-items-center rounded-full ${
                    isUnread ? "bg-primary text-primary-foreground" : "bg-muted text-ink-muted"
                  }`}
                >
                  <Bell className="h-4 w-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-extrabold text-ink">{notificationTitle(n)}</p>
                  <p className="mt-0.5 text-sm text-ink-muted">{notificationBody(n)}</p>
                  <p className="mt-1 text-[11px] uppercase tracking-wider text-ink-subtle">
                    {formatDateTime(n.created_at)}
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  {isUnread && (
                    <button
                      onClick={() => markRead.mutate(n.id)}
                      title="Mark as read"
                      className="rounded-lg p-2 text-ink-muted hover:bg-muted hover:text-ink"
                    >
                      <Check className="h-4 w-4" />
                    </button>
                  )}
                  <button
                    onClick={() => remove.mutate(n.id)}
                    title="Delete"
                    className="rounded-lg p-2 text-ink-muted hover:bg-destructive/10 hover:text-destructive"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    </PageShell>
  );
}
